import { useState, useEffect, useCallback } from "react";
import { clampSx, humanize } from "../../utils/adminFormat";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  InputAdornment,
  Grid,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Search,
  Visibility,
  Edit,
  Flag,
  Refresh as RefreshIcon,
  OpenInNew,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DOMPurify from 'dompurify';
import 'react-quill-new/dist/quill.snow.css';
import api from "../../api";
import HtmlWithLatex from "./components/HtmlWithLatex";
import { adminHasPermission } from "../../utils/adminPermissions";

const stripHtml = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = String(html);
  return (div.textContent || div.innerText || '').trim();
};

const FlaggedQuestions = () => {
  const canUnflag = adminHasPermission("unflag-questions");
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0); // 0 = All, 1 = CourseTest, 2 = MockExam, 3 = PastPaper

  const [flaggedQuestions, setFlaggedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [rowsPerPage] = useState(15);
  const [search, setSearch] = useState("");

  const [examOptions, setExamOptions] = useState([]);
  const [courseTestOptions, setCourseTestOptions] = useState([]);
  const [paperOptions, setPaperOptions] = useState([]);
  const [selectedExam, setSelectedExam] = useState("");
  const [reasonDialog, setReasonDialog] = useState({ open: false, reason: '' });

  const sourceType = activeTab === 1 ? 'course_test' : activeTab === 2 ? 'mock_exam' : activeTab === 3 ? 'past_paper' : '';

  const fetchFlaggedQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
      };
      if (search) params.search = search;
      if (sourceType) params.source_type = sourceType;
      if (selectedExam) params.exam_id = selectedExam;

      const res = await api.get("/admin/flagged-questions", { params });
      const data = res.data?.data;
      setFlaggedQuestions(data?.data || []);
      setTotal(data?.total || 0);
    } catch (err) {
      toast.error("Failed to load flagged questions");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, sourceType, selectedExam]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchFlaggedQuestions();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchFlaggedQuestions]);

  // Fetch mock exams for filter
  useEffect(() => {
    api.get("/admin/mock-exam", { params: { per_page: 100, order_by: "name" } })
      .then((res) => {
        const items = res.data?.data?.data || res.data?.data || [];
        setExamOptions(items.map((e) => ({ id: e.id, name: e.name })));
      })
      .catch(() => {});
  }, []);

  // Fetch course tests for filter
  useEffect(() => {
    api.get("/admin/assign/test", { params: { per_page: 100 } })
      .then((res) => {
        const items = res.data?.data?.data || res.data?.data || [];
        setCourseTestOptions(items.map((t) => ({ id: t.id, name: t.name })));
      })
      .catch(() => {});
  }, []);

  // Fetch past papers for filter
  useEffect(() => {
    api.get("/admin/paper", { params: { per_page: 200 } })
      .then((res) => {
        const items = res.data?.data?.data || res.data?.data || [];
        setPaperOptions(items.map((p) => ({ id: p.id, name: p.name })));
      })
      .catch(() => {});
  }, []);

  const handleUnflag = async (flagId) => {
    if (!canUnflag) return;
    try {
      await api.delete(`/admin/flagged-questions/${flagId}`);
      toast.success("Question unflagged successfully");
      fetchFlaggedQuestions();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to unflag question");
    }
  };

  const getSourceLabel = (type) => {
    switch (type) {
      case 'course_test':
        return <Chip label="Course Test" size="small" color="primary" variant="outlined" />;
      case 'mock_exam':
        return <Chip label="Mock Exam" size="small" color="secondary" variant="outlined" />;
      case 'past_paper':
        return <Chip label="Past Paper" size="small" color="success" variant="outlined" />;
      default:
        return <Chip label="Unknown" size="small" variant="outlined" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
            <Flag color="warning" />
            Flagged Questions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage questions flagged by students during exams.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchFlaggedQuestions}
        >
          Refresh
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => { setActiveTab(newValue); setPage(0); setSelectedExam(""); }}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
          }}
        >
          <Tab label="All Flagged" />
          <Tab label="Course Test" />
          <Tab label="Mock Exam" />
          <Tab label="Past Paper" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search questions or students..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          {activeTab === 2 && (
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Exam</InputLabel>
                <Select
                  value={selectedExam}
                  label="Filter by Exam"
                  onChange={(e) => { setSelectedExam(e.target.value); setPage(0); }}
                >
                  <MenuItem value="">All Exams</MenuItem>
                  {examOptions.map((exam) => (
                    <MenuItem key={exam.id} value={exam.id}>{exam.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {activeTab === 1 && (
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Course Test</InputLabel>
                <Select
                  value={selectedExam}
                  label="Filter by Course Test"
                  onChange={(e) => { setSelectedExam(e.target.value); setPage(0); }}
                >
                  <MenuItem value="">All Tests</MenuItem>
                  {courseTestOptions.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {activeTab === 3 && (
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Paper</InputLabel>
                <Select
                  value={selectedExam}
                  label="Filter by Paper"
                  onChange={(e) => { setSelectedExam(e.target.value); setPage(0); }}
                >
                  <MenuItem value="">All Papers</MenuItem>
                  {paperOptions.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ width: "100%" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 80 }}><strong>ID</strong></TableCell>
                <TableCell><strong>Source</strong></TableCell>
                <TableCell><strong>Question</strong></TableCell>
                <TableCell><strong>Exam</strong></TableCell>
                <TableCell><strong>Student</strong></TableCell>
                <TableCell><strong>Flagged By</strong></TableCell>
                <TableCell><strong>Issues</strong></TableCell>
                <TableCell><strong>Reason</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                  ? [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(10)].map((_, j) => (
                        <TableCell key={j}><Skeleton variant="text" width={80} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                : flaggedQuestions.map((fq) => (
                    <TableRow key={fq.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.8rem" }}>
                          #{fq.question_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getSourceLabel(fq.source_type)}
                      </TableCell>
                      <TableCell sx={{ minWidth: 240, maxWidth: 320 }}>
                        <Box sx={{ maxHeight: 60, overflow: "hidden" }}>
                          <HtmlWithLatex html={fq.question_text || ""} />
                        </Box>
                        <Chip
                          label={humanize(fq.question_type)}
                          size="small"
                          sx={{ mt: 0.5, fontSize: "0.7rem" }}
                          color="info"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 200, maxWidth: 260 }}>
                        <Tooltip title={fq.exam_name || ""} placement="top-start" enterDelay={500}>
                          <Typography variant="body2" sx={clampSx(2)}>{fq.exam_name || "—"}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ minWidth: 170 }}>
                        <Typography variant="body2">{fq.student_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{fq.student_email}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 170 }}>
                        <Typography variant="body2">{fq.flagged_by_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{fq.flagged_by_email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {fq.issue_question && <Chip label="Question" size="small" color="error" variant="outlined" sx={{ fontSize: '0.7rem' }} />}
                          {fq.issue_answer   && <Chip label="Answer"   size="small" color="warning" variant="outlined" sx={{ fontSize: '0.7rem' }} />}
                          {fq.issue_image    && <Chip label="Image"    size="small" color="info" variant="outlined" sx={{ fontSize: '0.7rem' }} />}
                          {!fq.issue_question && !fq.issue_answer && !fq.issue_image && (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 180 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              flex: 1,
                            }}
                          >
                            {stripHtml(fq.reason) || '—'}
                          </Typography>
                          {(fq.reason || '').length > 0 && (
                            <Tooltip title="View full reason">
                              <IconButton
                                size="small"
                                onClick={() => setReasonDialog({ open: true, reason: fq.reason || '' })}
                                sx={{ p: 0.25, color: 'primary.main' }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {fq.flagged_at ? new Date(fq.flagged_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fq.flagged_at ? new Date(fq.flagged_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {(fq.source_type === 'mock_exam' || fq.source_type === 'past_paper') ? (
                          <>
                            <Tooltip title={fq.qb_question_id ? "View in Question Bank" : "Not linked to Question Bank"}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!fq.qb_question_id}
                                  onClick={() => navigate(`/admin/question-bank/${fq.qb_question_id}/edit`)}
                                  sx={{ color: "#3f2b96" }}
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={fq.qb_question_id ? "Edit in Question Bank" : "Not linked to Question Bank"}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!fq.qb_question_id}
                                  onClick={() => navigate(`/admin/question-bank/${fq.qb_question_id}/edit`)}
                                  sx={{ color: "#1976d2" }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        ) : (
                          <Tooltip title="Open Course Test to review & edit question">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/admin/course-test/${fq.source_id}`)}
                              sx={{ color: "#3f2b96" }}
                            >
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canUnflag && (
                        <Tooltip title="Unflag">
                          <IconButton
                            size="small"
                            onClick={() => handleUnflag(fq.id)}
                            sx={{ color: "#ed6c02" }}
                          >
                            <Flag fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              {!loading && flaggedQuestions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Flag fontSize="large" color="action" />
                      <Typography color="text.secondary">No flagged questions found.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[15]}
        />
      </Paper>

      {/* Reason popup dialog */}
      <Dialog
        open={reasonDialog.open}
        onClose={() => setReasonDialog({ open: false, reason: '' })}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 0 }}>Flag Reason</DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Box
            className="ql-editor"
            sx={{ p: 0, minHeight: 40, '& p': { margin: 0 }, '& img': { maxWidth: '100%', borderRadius: 1 } }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(reasonDialog.reason || '<p>No reason provided.</p>') }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setReasonDialog({ open: false, reason: '' })} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FlaggedQuestions;
